import { SiteUrls } from '@tableau/shared-urls';
import { TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { AdminInsightsGetConfigurationRequest } from '~src/settings/IntegrationsSettings/serverApi/requests/AdminInsightsGetConfigurationRequest';
import { Thumbnail, ThumbnailProps } from '~src/spacesPlatform/components/card/Thumbnail';
import { SpaceHeaderContent } from '~src/spacesPlatform/components/header/SpaceHeaderContent';
import { SpaceSubHeaderContent } from '~src/spacesPlatform/components/header/SpaceSubheaderContent';

import { Messages } from '../../codegen/Localize';
import { BrowserTitleService } from '../../commonInfra/services/BrowserTitleService';
import { HelpService } from '../../commonInfra/services/HelpService';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { IAdminInsightsResult } from '../../settings/IntegrationsSettings/serverApi/types/IAdminInsightsResult';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import { AdminAnalysisTable } from './AdminAnalysisTable';

export interface AdminSiteStatusState {
  thumbnailProps: ThumbnailProps;
  navigateUrl: string;
}

export interface AdminSiteStatusProps {
  siteName: string;
}


export class AdminSiteStatus extends React.PureComponent<AdminSiteStatusProps, AdminSiteStatusState> {
    private projectId: number;
    private isAdminDataSourceEnabled: boolean;
    private learnMoreHref: string;
    private siteUrls: SiteUrls;
    private publishFrequency?: number;

    constructor(props: AdminSiteStatusProps) {
        super(props);
        this.state = {
            thumbnailProps: {
                navigateUrl:  null,
                defaultImageUrl: null,
                imageCoverBackground: true,
                'aria-label': Messages.adminInsightsProjectName(),
                allowDragAndDrop: false,
            },
            navigateUrl: null
        };
    }

    private server: SessionManagementService = getSessionManagementServiceInstance();

    public componentDidMount() {
        this.setNavigationUrlAndPublishFrequencyConfiguration();
    }

    private hideStatsForLoadTimes  = () => {
        return ServerSettingsUnauthenticated.hideAdminVizStatsForLoadTimes();
      };

    private hidePerformanceofViews  = () => {
        return ServerSettingsUnauthenticated.hideAdminVizPerformanceofViews();
    };

    private isOnlineEnvironment = () => {
        return this.server.isOnlineEnvironment();
    };

    private hidePerformanceofFlowRunsOnSite  = () => {
        return this.server.hideAdminVizPerformanceofFlowRunsOnSite();
    };

    private bridgeEnabled = () => ServerSettingsUnauthenticated.isBridgeEnabled();

    private analysisUrl = (sheetUrlId: string) => {
        return  this.siteUrls?.siteStatusVizUrl(sheetUrlId);
    };

    private buildThumbnailProps(navigateUrl: string): ThumbnailProps {
        return {
            navigateUrl,
            defaultImageUrl: 'img/project-thumb-2.png',
            imageCoverBackground: true,
            'aria-label': Messages.adminInsightsProjectName(),
            allowDragAndDrop: false,
        };
    }

    private adminInsightsCallbackWithPublishFrequency = (result: IAdminInsightsResult) => {
        if(this.adminInsightsProjectExists(result)){
            this.projectId = result.projectId;
            this.publishFrequency = result.publishFrequency;
            const navigateUrl = this.siteUrls?.projectUrl('' + this.projectId);
            this.setState({
                navigateUrl,
                thumbnailProps: this.buildThumbnailProps(navigateUrl),
            });
        } else {
            this.setState({navigateUrl: this.siteUrls?.projectUrl('-1')});
        }
    };

    private setNavigationUrlAndPublishFrequencyConfiguration = () => {
        if (this.server.isAdminDataSourceEnabled()) {
            this.isAdminDataSourceEnabled = true;
            this.learnMoreHref = HelpService.getServerHelpUrlByCategory('insights');
            WebHttpClient.sendRequest(new AdminInsightsGetConfigurationRequest()).
                then(result => {
                    this.adminInsightsCallbackWithPublishFrequency(result);
                });
        }
    };

    private adminInsightsProjectExists = (result: IAdminInsightsResult): boolean => {
        return result.projectId && result.projectId !== -1;
    };

    private renderAdminInsights = (): JSX.Element  => {
        return this.isAdminDataSourceEnabled ?  (
            <>
            <div className="tb-settings-section">
                <div className="tb-settings-group-name">
                    <SpaceSubHeaderContent subheaderText={Messages.adminInsightsHeader()}/>
                </div>
                <div className="tb-settings-msg">
                    {Messages.adminInsightsMessageOne()}
                    <span className="tb-settings-msg">
                        &nbsp;
                        <a href={this.learnMoreHref} target="_blank" tb-test-id="admin-insights-learn-more-link" rel="noopener noreferrer">
                            {Messages.adminInsightsLearnMore()}
                        </a>
                    </span>
                </div>
                <div className="tb-indent">
                    <div className="tb-settings-msg">
                        {this.renderProjectThumbNail()}
                    </div>
                    <span>&nbsp;</span>
                    <div>
                        {this.renderCurrentAdminInsightsPublishFrequencyConfiguration()}
                    </div>
                </div>
            </div>
            </>
        ) : null;
    };

    private renderProjectThumbNail = (): JSX.Element => {
        return (this.projectId && this.projectId !== -1) ?
            (<>
                <div className="tb-indent">
                    <div className="tb-settings-subgroup-name">
                        {Messages.adminInsightsShowProjectHeader()}
                    </div>
                    <div className="tb-settings-msg">
                        {Messages.adminInsightsShowProjectMsg()}
                    </div>
                    <div className="tb-thumbnail-admin-insights">
                        <Thumbnail {...this.state.thumbnailProps} />
                    </div>
                </div>
            </>): null;
    };

    private renderCurrentAdminInsightsPublishFrequencyConfiguration = (): JSX.Element  => {
        return (this.publishFrequency === 0 || this.publishFrequency === 1) ?
            (
            <div className="tb-indent">
                <div className="tb-settings-subgroup-name">
                    {Messages.adminInsightsPublishFrequencyConfigurationHeader()}
                </div>
                <div className="tb-settings-msg">
                    {this.publishFrequency
                        ? Messages.adminInsightsPublishFrequencyConfiguration({PUBLISH_FREQUENCY: Messages.weeklyLower()})
                        : Messages.adminInsightsPublishFrequencyConfiguration({PUBLISH_FREQUENCY: Messages.dailyLower()})
                    }
                    &nbsp;
                    {
                      <TextLinkWidget
                      href={this.siteUrls?.settingsUrl()}
                      testId={'admin-insights-publish-frequency-configuration'}
                      >
                        {Messages.settings()}
                      </TextLinkWidget>
                    }
                </div>
            </div>): null;
    };

    private renderAdminAnalysisTable(): JSX.Element {
       return (
       <>
            <div className="tb-settings-section">
                <div className="tb-settings-group-name">
                    <SpaceSubHeaderContent subheaderText={Messages.analysisTableHeaderAnalysis()}/>
                </div>
                <div className="tb-settings-msg">
                    {Messages.analysisSiteDetails()}
                </div>
                <AdminAnalysisTable
                    hideStatsForLoadTimes={this.hideStatsForLoadTimes()}
                    hidePerformanceofViews={this.hidePerformanceofViews()}
                    hidePerformanceofFlowRunsOnSite={this.hidePerformanceofFlowRunsOnSite()}
                    isOnlineEnvironment={this.isOnlineEnvironment()}
                    isBridgeEnabled={this.bridgeEnabled()}
                    analysisUrl={this.analysisUrl}
                    inServerArea={false}
                    navigateUrl={this.state.navigateUrl}
                    isAdminDataSourceEnabled={this.isAdminDataSourceEnabled}
                />
            </div>
       </>);
    }

    public render(): JSX.Element {
        this.siteUrls = new SiteUrls({
            siteUrlName: this.props.siteName,
            queryString: window.location.search
        });
        BrowserTitleService.setTitleWithTranslatedSpace({translatedSpaceName: Messages.siteStatus()});
        return (
        <>
        <div className="tb-tabs-banner">
            <span className="tb-tabs-banner-title">
                <SpaceHeaderContent headerText={Messages.siteStatus()}/>
            </span>
        </div>
        <div className="tb-tabs-banner-subplace">
            <div className="tb-scroll tb-scroll-settings">
                <div className="tb-settings-container">
                    {this.renderAdminInsights()}
                    {this.renderAdminAnalysisTable()}
                </div>
            </div>
        </div>
        </>
        );
    }
}
